import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchProfileById } from '../lib/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event);
      
      // Only update user state if the user actually changed (different ID or logged out)
      const newUserId = session?.user?.id ?? null;
      
      setUser(prevUser => {
        // If IDs are the same, don't create a new object reference
        if (prevUser?.id === newUserId) {
          return prevUser;
        }
        return session?.user ?? null;
      });
      
      if (session?.user) {
        // For SIGNED_UP event, the profile might not exist yet (created by trigger)
        // Add a small delay and retry logic
        if (event === 'SIGNED_UP') {
          // Give the trigger time to create the profile
          setTimeout(() => fetchProfile(session.user.id), 1000);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Only fetch profile on actual sign in or token refresh, not on every visibility change
          await fetchProfile(session.user.id);
        }
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      // Use centralized API helper (workaround for Supabase client hanging)
      const profileData = await fetchProfileById(userId);
      if (profileData) {
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName, role = 'client', crafterMetadata = {}) => {
    // Use backend endpoint to bypass Supabase rate limits
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName,
          role,
          crafter_type: crafterMetadata.crafter_type || null,
          p_iva: crafterMetadata.p_iva || null,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Pydantic 422 restituisce data.detail come array di errori
        let msg = 'Errore durante la registrazione';
        if (Array.isArray(data?.detail)) {
          const first = data.detail[0];
          msg = first?.msg?.replace(/^Value error,\s*/, '') || msg;
        } else if (typeof data?.detail === 'string') {
          msg = data.detail;
        }
        return { data: null, error: { message: msg } };
      }
      
      return { data, error: null };
    } catch (err) {
      console.error('SignUp error:', err);
      return { data: null, error: { message: err.message || 'Errore di rete durante la registrazione' } };
    }
  };

  const completeOAuthProfile = async (email, fullName, role = 'client', crafterMetadata = {}, oauthInfo = {}) => {
    // Complete profile for OAuth users (user already exists in Supabase Auth)
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/complete-oauth-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          full_name: fullName,
          role,
          crafter_type: crafterMetadata.crafter_type || null,
          p_iva: crafterMetadata.p_iva || null,
          oauth_provider: oauthInfo.provider || null,
          oauth_provider_id: oauthInfo.providerId || null,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { data: null, error: { message: data.detail || 'Errore durante il completamento del profilo' } };
      }
      
      return { data, error: null };
    } catch (err) {
      console.error('Complete OAuth profile error:', err);
      return { data: null, error: { message: err.message || 'Errore di rete durante il completamento del profilo' } };
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: new Error('No user logged in') };
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (!error && data) {
      setProfile(data);
    }
    return { data, error };
  };

  const refreshProfile = async () => {
    if (!user) return;
    await fetchProfile(user.id);
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    completeOAuthProfile,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
